import { useState, useEffect } from "react";
import { useMode } from "@/context/ModeContext";
import { useNavigate } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../db";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, FileText, Search } from "lucide-react";
import TablePagination from "@/components/TablePagination";

import { usePagination } from "../hooks/usePagination";
import { formatRupees } from "../utils/currency";
import { MobileHeader } from "@/components/MobileHeader";
import { useUserRole } from "../hooks/useUserRole";
import { getFinancialScope } from "../utils/accessPolicy";
import PropTypes from 'prop-types';

export default function Invoices({ onLogout }) {
    const navigate = useNavigate();
    const userId = localStorage.getItem("user_id");

    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const { activeMode } = useMode();
    const { isReceptionistOnly, memberships } = useUserRole();
    const financialScope = getFinancialScope(activeMode, memberships, isReceptionistOnly);
    const financialClinicIdsKey = [...financialScope.includedClinicIds].sort().join("|");

    // Load invoices from local db
    const allInvoices = useLiveQuery(async () => {
        let invoices = [];

        if (activeMode === 'personal') {
            invoices = await db.invoices
                .where("doctor_id")
                .equals(userId)
                .filter(inv => !inv.clinic_id || inv.clinic_id === 'personal')
                .toArray();
        } else if (activeMode && activeMode !== 'everything') {
            // Clinic Mode
            invoices = await db.invoices
                .where("clinic_id")
                .equals(activeMode)
                .toArray();
        } else {
            // Everything: Filter using financialScope
            const all = await db.invoices.toArray();
            const includedClinicIds = new Set(financialScope.includedClinicIds);
            invoices = all.filter(inv => {
                const isPersonal = !inv.clinic_id || inv.clinic_id === 'personal';
                if (isPersonal) {
                    return financialScope.includePersonal && inv.doctor_id === userId;
                }
                return includedClinicIds.has(inv.clinic_id);
            });
        }

        // Enrich with patient names
        for (const inv of invoices) {
            const patient = await db.patients.get(inv.patient_id);
            if (patient) {
                inv.patient_name = patient.name;
            }
        }

        return invoices.sort((a, b) =>
            new Date(b.created_at) - new Date(a.created_at)
        );
    }, [userId, activeMode, financialScope.canAccessFinancials, financialScope.includePersonal, financialClinicIdsKey]);

    // Invoices are synced via SyncService (same as patients, appointments, facility_charges)

    // Filter invoices
    const filteredInvoices = allInvoices?.filter(inv => {
        const matchesSearch = !searchQuery ||
            inv.invoice_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            inv.patient_name?.toLowerCase().includes(searchQuery.toLowerCase());

        const matchesStatus = statusFilter === "all" || inv.status === statusFilter;

        return matchesSearch && matchesStatus;
    }) || [];

    // Pagination Hook
    const pagination = usePagination(filteredInvoices, {
        initialPageSize: 15,
        isMobile
    });

    const getStatusBadge = (status) => {
        const styles = {
            finalized: { background: "#dcfce7", color: "#16a34a" },
            cancelled: { background: "#fee2e2", color: "#dc2626" },
            draft: { background: "#fef3c7", color: "#d97706" }
        };
        const style = styles[status] || styles.draft;
        return (
            <span style={{
                padding: "0.25rem 0.75rem",
                borderRadius: "9999px",
                fontSize: "0.75rem",
                fontWeight: "500",
                ...style
            }}>
                {status?.toUpperCase()}
            </span>
        );
    };

    return (
        <div className="page-container" data-testid="invoices-page">
            <Sidebar active="invoices" onLogout={onLogout} />
            <div className="main-content">
                {/* Mobile Header with Mode Selector */}
                <MobileHeader title="Invoices">
                    <Button
                        onClick={() => navigate("/invoices/new")}
                        style={{ background: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)" }}
                        className="w-full mt-2"
                    >
                        <Plus size={20} className="mr-2" />
                        New Invoice
                    </Button>
                </MobileHeader>

                {/* Desktop Header - hidden on mobile */}
                <div className="header hidden md:flex items-center justify-between mb-6">
                    <h1>Invoices</h1>
                    <Button
                        onClick={() => navigate("/invoices/new")}
                        style={{ background: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)" }}
                        className="w-full md:w-auto"
                    >
                        <Plus size={20} style={{ marginRight: "0.5rem" }} />
                        New Invoice
                    </Button>
                </div>

                {/* Filters */}
                <div className="flex flex-col md:flex-row gap-4 mb-6">
                    <div className="relative flex-1 max-w-full md:max-w-[300px]">
                        <Search size={18} style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
                        <Input
                            placeholder="Search by invoice number or patient..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{ paddingLeft: "40px" }}
                        />
                    </div>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="w-full md:w-auto h-10 px-3 rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-sky-500 text-sm"
                    >
                        <option value="all">All Status</option>
                        <option value="finalized">Finalized</option>
                        <option value="cancelled">Cancelled</option>
                    </select>
                </div>

                {/* Invoice List */}
                {pagination.items.length === 0 ? (
                    <Card>
                        <CardContent style={{ padding: "3rem", textAlign: "center" }}>
                            <FileText size={48} style={{ color: "#94a3b8", marginBottom: "1rem" }} />
                            <h3 style={{ marginBottom: "0.5rem" }}>No invoices yet</h3>
                            <p style={{ color: "#64748b", marginBottom: "1.5rem" }}>
                                Create your first invoice to get started
                            </p>
                            <Button onClick={() => navigate("/invoices/new")}>
                                <Plus size={16} style={{ marginRight: "0.5rem" }} />
                                Create Invoice
                            </Button>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="card">
                        {/* Desktop Table View */}
                        <div className="table-container desktop-only">
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead>
                                    <tr>
                                        <th>Invoice #</th>
                                        <th>Patient</th>
                                        <th>Date</th>
                                        <th>Amount</th>
                                        <th>Status</th>

                                    </tr>
                                </thead>
                                <tbody>
                                    {pagination.items.map((invoice) => (
                                        <tr
                                            key={invoice.id}
                                            onClick={() => navigate(`/invoices/${invoice.id}`)}
                                            style={{ cursor: "pointer" }}
                                            className="hover:bg-slate-50 transition-colors"
                                        >
                                            <td className="font-medium text-sky-600">{invoice.invoice_number}</td>
                                            <td>
                                                <div className="font-medium">{invoice.patient_name || "Unknown"}</div>
                                            </td>
                                            <td>{new Date(invoice.issue_date).toLocaleDateString()}</td>
                                            <td className="font-medium">{formatRupees(invoice.total_amount)}</td>
                                            <td>{getStatusBadge(invoice.status)}</td>

                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Card View */}
                        <div className="mobile-only flex flex-col gap-4">
                            {pagination.items.map(invoice => (
                                <div
                                    key={invoice.id}
                                    onClick={() => navigate(`/invoices/${invoice.id}`)}
                                    style={{
                                        background: "#f8fafc",
                                        borderRadius: "8px",
                                        marginBottom: "0.75rem",
                                        padding: "1rem",
                                        borderLeft: "3px solid #0ea5e9",
                                        cursor: "pointer"
                                    }}
                                >
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                                        <span style={{ fontWeight: "600", color: "#0ea5e9" }}>{invoice.invoice_number}</span>
                                        {getStatusBadge(invoice.status)}
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.875rem", marginBottom: "0.25rem" }}>
                                        <span style={{ color: "#64748b" }}>Patient:</span>
                                        <span style={{ fontWeight: "500" }}>{invoice.patient_name || "Unknown"}</span>
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.875rem", marginBottom: "0.5rem" }}>
                                        <span style={{ color: "#64748b" }}>Date:</span>
                                        <span>{new Date(invoice.issue_date).toLocaleDateString()}</span>
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.875rem", paddingTop: "0.5rem", borderTop: "1px solid #f1f5f9" }}>
                                        <span style={{ fontWeight: "600" }}>Total:</span>
                                        <span style={{ fontWeight: "600" }}>{formatRupees(invoice.total_amount)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <TablePagination
                            pagination={pagination}
                            isMobile={isMobile}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

Invoices.propTypes = {
    onLogout: PropTypes.func
};
